import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Container,
  Section,
  SectionHead,
  Card,
  buttonStyles,
  fadeUp,
  stagger,
  inView,
} from "@comai-portal/design-system";

/**
 * OperatorsPage — the keystone door, written to control-first operators. (CO 069)
 *
 * The tribe that runs its own stack, paid the autonomy tax willingly, distrusts
 * convenience as a value. Voice: operator-to-operator, technical without
 * apology, no hand-holding. Acid test: a serious self-hosted Linux user reads
 * this and feels respected, not sold to.
 */
export function OperatorsPage() {
  return (
    <div className="bg-paper text-ink dark:bg-ink dark:text-paper">
      <Section>
        <Container width="prose" className="text-center">
          <motion.div variants={stagger} initial="hidden" animate="show">
            <motion.p variants={fadeUp} className="ds-eyebrow mb-5">
              For operators
            </motion.p>
            <motion.h1
              variants={fadeUp}
              className="font-display font-semibold tracking-[-0.02em] text-[clamp(2rem,5.5vw,3.75rem)] leading-[1.08] text-balance"
            >
              You run your own stack on purpose. So do we.
            </motion.h1>
            <motion.p
              variants={fadeUp}
              className="text-lead text-mute dark:text-mute-soft mt-6"
            >
              You didn't pick a hosted platform because you wanted control, and
              you paid for it in ops burden. ComOS is unified and federated —
              one architecture, no app seams inside; one endpoint, every
              merchant outside. We're not asking you to give up control. We're
              handing you the spec.
            </motion.p>
          </motion.div>
        </Container>
      </Section>

      <Section tint="sunk">
        <Container>
          <SectionHead
            eyebrow="What you actually get"
            title="The OS, not another app on top of a checkout."
          />
          <motion.div
            {...inView}
            variants={stagger}
            className="mt-12 grid gap-5 md:grid-cols-2"
          >
            {[
              {
                k: "One tenant, every platform",
                v: "Catalog, cart, orders, email, shipping, marketing, payments — composed in one OS under one identity. Not eight vendors and a pile of glue. The tenant is the spine; compose the platforms your business actually needs.",
              },
              {
                k: "Agent-callable by design",
                v: "MCP, UCP, and OpenAPI / REST terminate here as first-class citizens. One endpoint exposes the whole control plane. No scraping, no shim, no translation layer.",
              },
              {
                k: "Autonomy you dial, not autonomy you accept",
                v: "Off → Recommend → Confirm → Auto, per domain. Confidence-scored decisions, full audit trail, time-windowed rollback. The agents work for you under rules you set.",
              },
              {
                k: "No rent, no lock-in tax",
                v: "Set it all up free. Pay a small, transparent cut only when money moves — out and in. You're never underwater to us. Idle costs nothing.",
              },
            ].map((it) => (
              <motion.div key={it.k} variants={fadeUp}>
                <Card hover className="h-full">
                  <h3 className="text-display-sm mb-2">{it.k}</h3>
                  <p className="text-mute dark:text-mute-soft text-sm">
                    {it.v}
                  </p>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </Container>
      </Section>

      {/* A federation of federations — the frame, told to the control-first
          segment (CO 494 Phase 4). Both-halves rule: sovereignty never appears
          without the one-economy half in the same passage. No customer-node
          count is implied; the node class is described as the shipped
          capability it is, with its hardening status stated plainly. */}
      <Section>
        <Container>
          <SectionHead
            eyebrow="A federation of federations"
            title="A world of your own, inside one network."
            lead="Every business on ComOS runs a complete world — its own tenants, its own composed platforms, its own agents — and governs it: who it admits, what it composes, how far its agents act. What never forks is the economy: one mint, one ledger, one settlement rail, one attestation chain, no matter how deep the tree."
          />
          <motion.div
            {...inView}
            variants={stagger}
            className="mt-12 grid gap-5 md:grid-cols-2"
          >
            {[
              {
                k: "Your silicon is a first-class home",
                v: "The same runtime that serves our cloud runs as a self-hosted node on hardware you own — your world, under your roof. The node calls out; nothing calls in. No inbound port, no session, no socket held open to us.",
              },
              {
                k: "Sovereignty over the tree, membership in the economy",
                v: "Your world's operating state lives with you. Money truth lives at the hub and projects down read-only — a node cannot write the ledger, by construction. That split is what lets every world stay sovereign while the economy stays one.",
              },
            ].map((it) => (
              <motion.div key={it.k} variants={fadeUp}>
                <Card hover className="h-full">
                  <h3 className="text-display-sm mb-2">{it.k}</h3>
                  <p className="text-mute dark:text-mute-soft text-sm">
                    {it.v}
                  </p>
                </Card>
              </motion.div>
            ))}
          </motion.div>
          <motion.p
            {...inView}
            variants={fadeUp}
            className="mt-8 text-center text-mute dark:text-mute-soft"
          >
            Self-hosted nodes are in hardening now. The hosted door is open
            today, and a hosted tenant is the same world — same platforms, same
            agents, same economy.
          </motion.p>
          <motion.div
            {...inView}
            variants={fadeUp}
            className="mt-6 flex flex-col sm:flex-row gap-3 justify-center"
          >
            <Link
              to="/install"
              className={buttonStyles({ variant: "primary" })}
            >
              Run your own node
            </Link>
            <Link
              to="/blog/every-node-is-its-own-world"
              className={buttonStyles({ variant: "ghost" })}
            >
              Read the architecture series →
            </Link>
          </motion.div>
        </Container>
      </Section>

      <Section tint="sunk">
        <Container width="prose" className="text-center">
          <motion.div {...inView} variants={fadeUp}>
            <h2 className="text-display-md text-balance">
              We don't ask for your trust. We hand you the URL.
            </h2>
            <p className="text-lead text-mute dark:text-mute-soft mt-5">
              Every claim on this site is verifiable. The honesty ratio is
              published live — the rate at which claims hold up, visible even
              when it's unflattering. Evaluate us the way you'd evaluate any
              infrastructure — by reading it.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/start"
                className={buttonStyles({ variant: "primary", size: "lg" })}
              >
                Stand up a tenant — free
              </Link>
              <Link
                to="/trust"
                className={buttonStyles({ variant: "outline", size: "lg" })}
              >
                How we prove it
              </Link>
            </div>
          </motion.div>
        </Container>
      </Section>
    </div>
  );
}
